import type { Comment } from '@kizen/api/comment';
import type { CustomerNotification } from '@kizen/api/customer-notifications';
import type { QueryDataOf } from '@kizen/query';

import { createComment } from '@kizen/api/comment';
import { Button } from '@kizen/kds/Button';
import { Typography } from '@kizen/kds/Typography';
import styled from '@emotion/styled';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns/format';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuid } from 'uuid';
import { layers } from '__app/spacing';
import { parseMentions } from '__components/Inputs/NotesRichTextEditor';
import BasicModalWithConfirmation from '__components/Modals/presets/BasicModalWithConfirmation';
import { CommentBlock } from 'components/Timeline2/EventBlocks/CommentBlock';
import { useToast } from '__components/ToastProvider';
import { CommentEditor } from 'components/WYSIWYG/CommentEditor';
import { monitoringExceptionHelper } from 'instrumentation/helpers';
import { customerNotificationCommentsQuery } from 'query/customer-notifications';
import AxiosService from 'services/AxiosService';
import { useNotificationsPage } from './context';
import { NotificationStatus } from './NotificationStatus';

type CommentsModalProps = {
  notification: CustomerNotification;
  onHide(): void;
  onSaveComment(notificationId: string, comment: Comment): void;
};

const Modal = styled(BasicModalWithConfirmation)`
  .modal-body {
    padding-right: 20px;
    padding-left: 20px;
    padding-bottom: 10px;
  }
`;

export const CommentsModal = (props: CommentsModalProps) => {
  const { notification, onHide, onSaveComment } = props;
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { metadata } = useNotificationsPage();
  const [showToast] = useToast();
  const [text, setText] = useState('');
  const [isEmpty, setIsEmpty] = useState(true);
  const [editorKey, setEditorKey] = useState(uuid());

  const queryOptions = useMemo(() => {
    return customerNotificationCommentsQuery(notification.id, {
      staleTime: 0,
    });
  }, [notification.id]);
  const query = useQuery(queryOptions);
  const comments = query.data ?? [];

  const commentsMutation = useMutation({
    mutationFn: () => {
      const mentions = parseMentions(text);

      return createComment(AxiosService, {
        text,
        mentions,
        subject_id: notification.id,
        subject_type: 'subject_type_customer_notification',
      });
    },
    onError: (error) => {
      monitoringExceptionHelper(error, {
        extra: {
          notification_id: notification.id,
        },
      });

      showToast({
        message: t("Your comment couldn't be saved. Please try again."),
        variant: 'failure',
      });
    },
    onSuccess: (data) => {
      const { comments: _c, ...comment } = data;

      setText('');
      setIsEmpty(true);
      setEditorKey(uuid());

      qc.setQueryData<QueryDataOf<typeof queryOptions>>(
        queryOptions.queryKey,
        (prev) => {
          const next = [comment];
          return prev ? next.concat(prev) : next;
        }
      );

      onSaveComment(notification.id, comment);
    },
  });

  return (
    <Modal
      show
      hideFooter
      size="medium"
      heading={`${t('Comments')} - ${metadata.table.labels[notification.notification_type]}`}
      onHide={onHide}
    >
      <div className="flex flex-col gap-[20px]">
        <div className="flex flex-column gap-[15px]">
          <Typography>{notification.title}</Typography>
          <Typography>
            {t('Created')}: {format(notification.created, 'MM/dd/yyyy h:mm aa')}
          </Typography>
          <NotificationStatus notification={notification} />
        </div>
        <div className="grid grid-rows-[1fr_36px] gap-[20px]">
          <div>
            <CommentEditor
              key={editorKey}
              zIndex={layers.modals()}
              onChange={({ editor }) => {
                setIsEmpty(editor.isEmpty);
                setText(editor.getHTML());
              }}
            />
          </div>
          <div className="justify-self-end">
            <Button
              color="secondary"
              disabled={isEmpty || commentsMutation.isPending}
              onClick={() => commentsMutation.mutate()}
            >
              {t('Save')}
            </Button>
          </div>
        </div>
        <div className="flex flex-col gap-[20px]">
          {comments.map((comment) => (
            <CommentBlock key={comment.id} comment={comment} />
          ))}
        </div>
      </div>
    </Modal>
  );
};
